import { Link } from "react-router-dom";
import { Leaf, AlertTriangle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { LegalLinks } from "@/components/LegalLinks";
import { legalFor, PRIVACY_PUBLISHED, TERMS_PUBLISHED } from "@/i18n/legal";

// Texte avec passages [[à compléter / à vérifier]] surlignés et URLs cliquables.
const URL_RE = /(https?:\/\/[^\s)\]]+?)(?=[.,;:]?(?:\s|$|\)))/g;
function Linked({ text }) {
  return text.split(URL_RE).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4 break-all" data-testid="legal-external-link">{part}</a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

function Marked({ text }) {
  const parts = text.split(/(\[\[[^\]]*\]\])/g);
  return parts.map((part, i) =>
    part.startsWith("[[") ? (
      <mark key={i} className="rounded px-1 bg-amber-100 text-amber-950 dark:bg-amber-900/40 dark:text-amber-100" data-testid="legal-placeholder">{part.slice(2, -2)}</mark>
    ) : (
      <Linked key={i} text={part} />
    )
  );
}

function Section({ s, index, kind }) {
  return (
    <section id={`${kind}-s${index}`} className="chenia-card p-5 sm:p-6 scroll-mt-24" data-testid={`${kind}-section-${index}`}>
      <h2 className="font-serif text-lg md:text-xl font-semibold tracking-tight">{s.h}</h2>
      {s.p?.map((t, i) => <p key={i} className="mt-3 text-sm leading-relaxed"><Marked text={t} /></p>)}
      {s.ul && <ul className="mt-3 space-y-2 text-sm leading-relaxed list-disc pl-5">{s.ul.map((t, i) => <li key={i}><Marked text={t} /></li>)}</ul>}
      {s.p2?.map((t, i) => <p key={i} className="mt-3 text-sm leading-relaxed"><Marked text={t} /></p>)}
    </section>
  );
}

// kind : "privacy" | "terms"
export default function LegalPage({ kind }) {
  const { lang } = useLang();
  const legal = legalFor(lang);
  const doc = legal[kind];
  const { labels } = legal;
  const published = kind === "privacy" ? PRIVACY_PUBLISHED : TERMS_PUBLISHED;
  const other = kind === "privacy" ? { to: "/terms", label: labels.terms, on: TERMS_PUBLISHED } : { to: "/privacy", label: labels.privacy, on: PRIVACY_PUBLISHED };

  return (
    <div className="min-h-screen bg-background" data-testid={`${kind}-page`}>
      <header className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="legal-home-link">
          <span className="grid place-items-center h-9 w-9 rounded-2xl bg-primary text-primary-foreground"><Leaf className="h-5 w-5" /></span>
          <span className="font-serif text-xl font-semibold">CHENIA</span>
        </Link>
        <LanguageSelector />
      </header>

      <main className="max-w-3xl mx-auto px-5 pb-16">
        {!published && <div className="rounded-2xl border border-amber-300 bg-amber-50 text-amber-950 p-4 flex gap-3" data-testid="legal-draft-banner">
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-sm">{labels.draft_title}</p>
            <p className="text-sm mt-1">{labels.draft_desc}</p>
          </div>
        </div>}

        <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight mt-8" data-testid={`${kind}-title`}>{doc.title}</h1>
        <p className="mt-2 text-sm text-muted-foreground"><Marked text={labels.updated} /></p>
        {doc.intro.map((t, i) => <p key={i} className="mt-4 text-base leading-relaxed"><Marked text={t} /></p>)}

        <nav className="mt-6 chenia-card p-5" aria-label={labels.summary} data-testid={`${kind}-toc`}>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{labels.summary}</p>
          <ol className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {doc.sections.map((s, i) => <li key={i}><a href={`#${kind}-s${i + 1}`} className="hover:underline underline-offset-4">{s.h}</a></li>)}
          </ol>
        </nav>

        <div className="mt-6 space-y-4">
          {doc.sections.map((s, i) => <Section key={i} s={s} index={i + 1} kind={kind} />)}
        </div>

        <footer className="mt-10 pt-6 border-t border-border flex flex-wrap items-center justify-between gap-3">
          <LegalLinks testIdPrefix="legal-footer" />
          <div className="flex flex-wrap gap-4 text-xs">
            {other.on && <Link to={other.to} className="text-primary hover:underline underline-offset-4" data-testid="legal-other-link">{other.label}</Link>}
            <Link to="/delete-account" className="text-muted-foreground hover:underline underline-offset-4" data-testid="legal-delete-link">{labels.delete_account}</Link>
            <Link to="/" className="text-muted-foreground hover:underline underline-offset-4">{labels.back_home}</Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
